import Cookies from 'cookies-js';

import {
  BRAND_HAS_ADS_RESTRICTION,
  BRAND_HAS_DFP_DISPLAY_AUTO_REFRESH,
  BRAND_HAS_GO_IP_ADSERVER,
  BRAND_HAS_MQ_DFP_FIRST_LEVEL,
  BRAND_HAS_DM_PLAYER
} from 'common/configuration/constants';
import {
  DFP_BASE_URL,
  DISPLAY_PROVIDER,
  INHIBITION_AD_ID,
  newsContext,
  PROGRAMMATIC_ADVERTISER_IDS,
  slideshowContext,
  longreadContext,
  STICKY_POS_HOLDER_CLASS,
  targetATF,
  targetBTF,
  targetMTF,
  targetPromo1
} from 'common/constants/Ads';
import { COOKIE_INFO_COOKIE } from 'common/constants/CookieNames';
import { AdProviders } from 'common/module/advertisement/types';
import {
  filterAdsIdArray,
  getAdTrustContentHeight,
  getAdTrustVideoDurationStepTargeting,
  getBlueKaiKeywords,
  getMediarithmicsKeywords,
  getMqState,
  getApKeywords,
  getTagIdByGeoIp,
  handleIAS,
  prepareAdsPosition,
  triggerAdsPositionsReady
} from 'common/module/advertisement/utils';
import eventEmitter, { EventsTypes } from 'common/services/events/eventEmitter';
import loadGoogleDfp from 'common/third_party/googledfp';
import { addClass, removeClass } from 'common/tools/dom/classes';
import { isProd } from 'common/tools/env';
import { stringify } from 'common/tools/network/query-string';
import { getRootDomain } from 'common/tools/network/url';
import noop from 'common/tools/noop';
import get from 'common/tools/objects/get';
import trans from 'common/tools/translations/trans';
import { runAfterDeferAssets } from 'common/tools/ui/load-assets';

const CORRELATOR = Math.round(Math.random() * 10000000000);

// global GPT/DFP info (GTP is the name of the ads display for DFP)
let GPT_LIST_ID = window.GptIds; // list of ads id position
const GPT_CONFIG_PAGE = window.GptConfig; // object of all settings for the page (ads position config / ads position sizes / gpt tag / gpt keyword / ...)

// ADS Element and ID
const promo1Item = document.getElementsByClassName('js-autopromo')[0];
const inreadItem = document.getElementsByClassName('js-inread')[0];

const atfItem = document.getElementsByClassName('js-atf')[0];
const mtfItem = document.getElementsByClassName('js-mtf')[0];
const btfItem = document.getElementsByClassName('js-btf')[0];

const arrayTarget = [targetATF, targetMTF, targetBTF, targetPromo1];
const arrayPosition = [atfItem, mtfItem, btfItem, promo1Item]; // ADS position referer for changing position

// get bidding environment.
const useHeaderBidding =
  GPT_CONFIG_PAGE &&
  GPT_CONFIG_PAGE.prebid &&
  GPT_CONFIG_PAGE.prebidUrl &&
  GPT_CONFIG_PAGE.apstag;

const usePrebidStandAlone =
  GPT_CONFIG_PAGE &&
  GPT_CONFIG_PAGE.prebid &&
  GPT_CONFIG_PAGE.prebidUrl &&
  !GPT_CONFIG_PAGE.apstag;

// AD SERVER DISABLED
const AD_SERVER_DISABLED = get(GPT_CONFIG_PAGE, [
  'targeting',
  'ad_server_disabled'
]);

// ads array that we'll refresh every xx seconds
const VALIDATION_AUTOREFRESH_VIEW_DURATION = 5000;
const DIRECT_REFRESH_INTERVAL = get(GPT_CONFIG_PAGE, [
  'refresh-direct-interval'
]);
const PROGRAMMATIC_REFRESH_INTERVAL = get(GPT_CONFIG_PAGE, [
  'refresh-programmatic-interval'
]);

const adsToAutoRefresh = {};

// const used for specific condition on showing ads pos with google restriction on some brand
const DFP_INTERSTITIAL = 'dfp-interstitial';
const DFP_PULSE = 'dfp-pulse';
const DFP_MASTHEAD = 'dfp-masthead';

const ADS_RESTRICTION =
  BRAND_HAS_ADS_RESTRICTION && !Cookies.get(COOKIE_INFO_COOKIE);

const CAPPING_COOKIE_PREFIX = 'capping-';

const slots = {};

let dfpDisplayTag;
let mastheadLineId = null;
let headerBiddingModule;

export function init(resolve) {
  if (
    DISPLAY_PROVIDER !== AdProviders.DFP_SYSTEM_CODE ||
    !GPT_LIST_ID ||
    !GPT_LIST_ID.length ||
    AD_SERVER_DISABLED
  ) {
    if (!isProd()) {
      if (AD_SERVER_DISABLED) {
        // eslint-disable-next-line no-console
        console.warn(
          'DFP display init not engaged because of status Ad Serving Disabled on this page'
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn('DFP display init not engaged');
      }
    }
    resolve();
    return false;
  }

  // Remove ad postion that has any restriction (product or capping);
  GPT_LIST_ID = filterAdsPositionRestriction();

  runAfterDeferAssets(loadGoogleDfp);

  eventEmitter.on(EventsTypes.ADS_READY, initAutoRefresh);

  eventEmitter.on(EventsTypes.ASYNCHRONOUS_PAGE_CHANGE, refreshAds);

  // start
  callAds().then(resolve);
}

async function callAds() {
  // We define and handle each slot for the current page
  // The entry point is still the GPT_LIST_ID
  if (!GPT_LIST_ID || !GPT_LIST_ID.length || !GPT_CONFIG_PAGE) {
    if (!isProd()) {
      // eslint-disable-next-line no-console
      console.warn(
        'DFP callAds error either GPT_LIST_ID or GPT_CONFIG_PAGE is missing or empty'
      );
    }

    return false;
  }

  let dfpTagId = GPT_CONFIG_PAGE['tag-id'];

  if (BRAND_HAS_GO_IP_ADSERVER) {
    dfpTagId = await getTagIdByGeoIp(dfpTagId);
  }

  // We split the dfpDisplayTag in 3 parts to handle the possibility of dynamically change the account id part with another account id
  // It's a need from the ES x Latam ads managment but could be use for other stuff...
  dfpDisplayTag = dfpTagId + GPT_CONFIG_PAGE['tag-other-levels'];

  // For each ad slot present in the page, defined by id, in GPT_LIST
  // we will get the necessary configuration present in GPT_CONFIG_PAGE
  // /!\ Slideshow is not present in the page so it wont be set here but when called by createPosition if needed ...
  // /!\ ... The base config of the slideshow is in GPT_CONFIG_PAGE and hardset in the DFP.php file
  GPT_LIST_ID.forEach(item => {
    if (!GPT_CONFIG_PAGE[item]) {
      return false;
    }

    // go set ad position with everything DFP need (targetting / size / defineSlot ... )
    setPosition(item);
  });

  // Get content height (only for newspage for the moment => Digital Ad Trust Compliency)
  const contentHeightCase = getAdTrustContentHeight([
    document.getElementById('article-container'),
    document.getElementById('article-title')
  ]);

  // We move ads for mobile
  prepareAdsPosition(
    {
      news: newsContext,
      slideshow: slideshowContext,
      longread: longreadContext
    },
    inreadItem,
    arrayPosition,
    arrayTarget
  );

  // We set the page targeting if any also specified in GPT_CONFIG_PAGE :
  if (GPT_CONFIG_PAGE.targeting) {
    setPageTargeting({
      ...GPT_CONFIG_PAGE.targeting,
      ...(contentHeightCase ? { content_height_case: contentHeightCase } : {})
    });
  }

  window.googletag.cmd.push(() => {
    window.googletag.pubads().disableInitialLoad();
    window.googletag.pubads().collapseEmptyDivs(true);
    window.googletag.pubads().enableSingleRequest();
    window.googletag.enableServices();
  });

  if (GPT_CONFIG_PAGE && GPT_CONFIG_PAGE['ias-display-id']) {
    // If brand use IAS pluggin it's IAS that will trigger the render ... => ALLOCINE-9037
    await handleIAS(GPT_CONFIG_PAGE, GPT_LIST_ID, renderAds);
  } else {
    // Once every step is done, we render the ads like if code was write in the DOM :
    renderAds(GPT_LIST_ID);
  }

  triggerAdsPositionsReady();

  if (usePrebidStandAlone || useHeaderBidding) {
    headerBiddingModule = await import(
      /* webpackChunkName: "DfpHeaderBidding" */ './DfpHeaderBidding.lazy'
    );

    // Header bidding Prebid
    if (usePrebidStandAlone) {
      await headerBiddingModule.callStandAlonePrebid(
        GPT_CONFIG_PAGE,
        GPT_LIST_ID
      );
      return;
    }

    // Header bidding Amazon apstag & Prebid
    if (useHeaderBidding) {
      headerBiddingModule.callHeaderBidding(GPT_CONFIG_PAGE, GPT_LIST_ID);
      return;
    }
  }

  // Because of Prebid we have to disableInitialLoad of the ads.
  // That means that the display won't show the ads,
  // we have to "refresh" the positions (refresh with no param do it by itseilf)
  // In case of Prebid it's initAdserverWithPrebid that will trigger the refresh. Else we do it here.
  window.googletag.cmd.push(() => {
    window.googletag.pubads().refresh();
  });
}

function renderAds(arrayAd = []) {
  // We now render all ads by passing to the render function the array of ads id of the ad to display

  // render ads :
  arrayAd.forEach(item => {
    window.googletag.cmd.push(function gptPush() {
      window.googletag.display(item);
    });
  });
}

function setPosition(item) {
  window.googletag.cmd.push(() => {
    const adSlotConfig = GPT_CONFIG_PAGE[item];
    const defaultAdSlotSizeArray = adSlotConfig.sizes
      ? adSlotConfig.sizes.default.ad_size
      : null;
    let adSlot;

    // We define classique base slot :
    adSlot = window.googletag.defineSlot(
      dfpDisplayTag,
      defaultAdSlotSizeArray,
      item
    );

    if (adSlotConfig.outOfPage) {
      adSlot = window.googletag.defineOutOfPageSlot(dfpDisplayTag, item);
    }

    // We set slot targeting if any :
    if (adSlotConfig.targeting) {
      setAdSlotTargeting(adSlotConfig.targeting, adSlot);
    }

    // We set size mapping is we have size information (mandatory for defineSlot, not for defineOutOfPageSlot) :
    if (defaultAdSlotSizeArray) {
      adSlot.defineSizeMapping(setAdSlotSizeMapping(adSlotConfig.sizes));
    }

    // We add the slot to dfp/gpt
    adSlot.addService(window.googletag.pubads());

    // Trix to set a class on filled ads.
    window.googletag.pubads().addEventListener('slotRenderEnded', e => {
      const adElement = document.getElementById(e.slot.getSlotElementId());

      if (!e.isEmpty && e.advertiserId !== INHIBITION_AD_ID) {
        const currentId = e.slot.getSlotElementId();

        // If the ad isn't empty
        addClass(adElement, 'filled');

        // We add the key trad to be used by css for text in placeholder
        if (['dfp-atf', 'dfp-btf', 'dfp-mtf'].indexOf(currentId) !== -1) {
          addClass(adElement, 'ad-titled');
          adElement.setAttribute('data-title', trans('ads_placeholder.title'));
        }

        if (currentId == 'dfp-masthead' && adElement.parentNode) {
          addClass(adElement.parentNode, 'children-filled');
        }

        if (BRAND_HAS_DFP_DISPLAY_AUTO_REFRESH) {
          const isProgrammatic =
            PROGRAMMATIC_ADVERTISER_IDS[window.AC.config.brand].indexOf(
              e.advertiserId
            ) !== -1 || e.advertiserId === '';

          if (!isProgrammatic && currentId === DFP_MASTHEAD) {
            mastheadLineId = e.lineItemId;
          }

          if (GPT_CONFIG_PAGE[currentId].autoRefresh) {
            if (
              !isProgrammatic &&
              mastheadLineId &&
              e.lineItemId === mastheadLineId
            ) {
              GPT_CONFIG_PAGE[currentId].autoRefresh = false;
              return false;
            }

            adsToAutoRefresh[`${currentId}`] = {
              positionId: currentId.split('dfp-')[1],
              adElement: adElement,
              isProgammatic: isProgrammatic,
              refreshAutorized: false,
              intervalToValidation: VALIDATION_AUTOREFRESH_VIEW_DURATION,
              inView: false,
              startTime: null,
              timer: null
            };
          }
        }

        if (
          GPT_CONFIG_PAGE[currentId] &&
          GPT_CONFIG_PAGE[currentId].cappingExpireDays
        ) {
          Cookies.set(CAPPING_COOKIE_PREFIX + currentId, true, {
            expires: GPT_CONFIG_PAGE[currentId].cappingExpireDays,
            domain: '.' + getRootDomain(window.location.hostname)
          });
        }
      } else {
        removeClass(
          adElement.parentElement.parentElement,
          STICKY_POS_HOLDER_CLASS
        );
      }
    });

    window.googletag.pubads().addEventListener('slotVisibilityChanged', e => {
      const currentId = e.slot.getSlotElementId();

      if (
        BRAND_HAS_DFP_DISPLAY_AUTO_REFRESH &&
        GPT_CONFIG_PAGE[currentId].autoRefresh
      ) {
        handleAutoRefreshValidation(currentId, e.inViewPercentage >= 60);
      }
    });

    // store all the adSlot to use them with refresh if needed
    slots[`${item}`] = adSlot;
  });
}

function setAdSlotSizeMapping(adSlotSizeArray) {
  const mapping = window.googletag.sizeMapping();
  Object.values(adSlotSizeArray).forEach(value => {
    mapping.addSize(value.viewport_size, value.ad_size);
  });

  return mapping.build();
}

function setAdSlotTargeting(targetingObj, adSlot) {
  Object.entries(targetingObj).forEach(([key, value]) => {
    adSlot.setTargeting(key, value);
  });
}

function setNavEggTargeting(video = false) {
  if (!GPT_CONFIG_PAGE || !GPT_CONFIG_PAGE.navEgg) {
    return [];
  }

  let name;
  let col;
  const persona = JSON.parse(
    window.localStorage.getItem(`nvgpersona${GPT_CONFIG_PAGE.navEgg}`)
  );

  // display targeting handling for navegg
  if (persona && !video) {
    window.googletag.cmd.push(() => {
      for (col in persona) {
        if (col) {
          name = 'nvg_' + col;
          if (typeof window.googletag === 'object') {
            window.googletag.pubads().setTargeting(name, persona[col]);
          }
          if (typeof window.GA_googleAddAttr === 'function') {
            window.GA_googleAddAttr(name, persona[col]);
          }
        }
      }
    });
  } else {
    if (!persona) {
      return [];
    }

    // video prerool targeting handling for navegg
    const tmpValues = [];
    for (col in persona) {
      if (col) {
        name = 'nvg_' + col;
        tmpValues.push(`${name}=${persona[col]}`);
      }
    }

    return tmpValues;
  }
}

function setPageTargeting(targetingObj) {
  let targetingObject = { ...targetingObj };

  // HASH KEYWORD FOR AD TESTING OR CLIENT DEMO
  const HASH_KEYWORD_TARGET = document.location.hash;

  if (HASH_KEYWORD_TARGET) {
    const keywords = HASH_KEYWORD_TARGET.split('#')[1].split('=');

    if (keywords.length) {
      targetingObject[keywords[0]] = keywords[1];
    }
  }

  // Mediarithmics SEGMENTATION PART
  const MediarithmicsSegs = getMediarithmicsKeywords();

  targetingObject = {
    ...targetingObject,
    ...(MediarithmicsSegs.length ? { mics_webediaMics: MediarithmicsSegs } : {})
  };

  targetingObject = { ...targetingObject };

  // BLUEKAI SEGMENTATION PART
  const blukaiSegs = getBlueKaiKeywords();

  targetingObject = {
    ...targetingObject,
    ...(blukaiSegs ? { bluekai: [blukaiSegs] } : {})
  };

  // NAVEGG
  setNavEggTargeting();

  // TAILTARGET KEYWORDS
  if (window._ttprofiles) {
    const profile = window._ttprofiles;
    window.googletag.cmd.push(() => {
      window.googletag
        .pubads()
        .setTargeting('tge', profile.getEquipment)
        .setTargeting('tgp', profile.getProfiles)
        .setTargeting('tgu', profile.getMicrosegments)
        .setTargeting('tgc', profile.getSocialClass)
        .setTargeting('tga', profile.getAge)
        .setTargeting('tgg', profile.getGender)
        .setTargeting('tgs', profile.getSubjects)
        .setTargeting('tgca', profile.getCustomAudience);
    });
  }

  // use for prebig or other stuff that could need the complete targeting (with navegg tailtarget etc ...)
  window.GPT_TARGETING_OBJ = targetingObject;

  window.googletag.cmd.push(() => {
    Object.entries(targetingObject).forEach(([key, value]) => {
      window.googletag.pubads().setTargeting(key, value);
    });
  });
}

export function createPosition(positionId, referenceElement) {
  const item = `dfp-${positionId}`;

  // AJAX case where we inject serveral time the same position
  if (slots[item]) {
    window.googletag.destroySlots([slots[item]]);
    delete slots[item];
  }

  const element = document.createElement('div');
  element.id = item;
  referenceElement.appendChild(element);

  setPosition(item);

  window.googletag.cmd.push(() => {
    window.googletag.display(item);
  });
}

export function refreshAds(
  payload,
  all = false,
  withHeaderBidding = false,
  callback = noop
) {
  if (!payload.advertisementTargets) {
    return;
  }

  if (all && !withHeaderBidding) {
    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh();
    });

    return false;
  }

  const slotsToRefresh = [];
  const idsToRefresh = [];

  // get the right ads to refresh and push it in an array (if ads position is present on the page)
  payload.advertisementTargets.forEach(item => {
    const id = `dfp-${item}`;
    const slot = slots[id];

    if (!slot) {
      return false;
    }
    // ids are used to trigger HB refresh using callHeaderBidding
    idsToRefresh.push(id);
    // slots are used by dfp to trigger refresh of an Adunit ( ... a slot)
    slotsToRefresh.push(slot);
  });

  // no ads position to refresh
  if (!slotsToRefresh.length) {
    return false;
  }

  if (withHeaderBidding && useHeaderBidding && headerBiddingModule) {
    // Here we clear all targeting set by bidder to the positions.
    // That way when it refresh, there is no bid set and no error on news bidder value
    idsToRefresh.forEach(item => {
      slots[item].clearTargeting();
      setAdSlotTargeting(GPT_CONFIG_PAGE[item].targeting, slots[item]);
    });

    headerBiddingModule.callHeaderBidding(
      GPT_CONFIG_PAGE,
      idsToRefresh,
      slotsToRefresh,
      callback
    );
    return false;
  }

  // refresh asked ads
  window.googletag.cmd.push(() => {
    window.googletag.pubads().refresh(slotsToRefresh);
  });

  // callback if any or noop
  callback();
}

function handleAutoRefreshValidation(item, inView) {
  // return if view state already setted
  if (inView === adsToAutoRefresh[item].inView) {
    return;
  }

  // else set view state (or if changed)
  adsToAutoRefresh[item].inView = inView;

  // If refreshAutorized is true we doesn't need to go further until this position is refresh (in handleAutoRefreshAction)
  if (adsToAutoRefresh[item].refreshAutorized) {
    return;
  }

  // item isn't in refresh autorization zone so we have check if ...
  const interval = adsToAutoRefresh[item].intervalToValidation;

  if (inView) {
    // ... it's in view and trigger the countdown.
    adsToAutoRefresh[item].startTime = Date.now();

    // at the end of the timeout it means that the countdown is
    // finished and refresh autorization will be set to true.
    // also, all the params correposnding to the countdown will be reset to original value
    adsToAutoRefresh[item].timer = setTimeout(() => {
      adsToAutoRefresh[item].refreshAutorized = true;
      adsToAutoRefresh[item].timer = null;
      adsToAutoRefresh[item].startTime = null;
      adsToAutoRefresh[item].intervalToValidation = 0;
    }, interval);
  }

  if (!inView) {
    // we stop the countdown and store the value remaining before let refresh be autorized
    // off course we also set that item is not in view anymore
    if (adsToAutoRefresh[item].timer) {
      clearTimeout(adsToAutoRefresh[item].timer);
      adsToAutoRefresh[item].timer = null;
    }

    adsToAutoRefresh[item].intervalToValidation = adsToAutoRefresh[item]
      .startTime
      ? interval - (Date.now() - adsToAutoRefresh[item].startTime)
      : VALIDATION_AUTOREFRESH_VIEW_DURATION;
  }
}

function handleAutoRefreshAction(programmatic) {
  for (const [key, value] of Object.entries(adsToAutoRefresh)) {
    if (
      value.inView &&
      value.refreshAutorized &&
      programmatic === value.isProgammatic
    ) {
      refreshAds(
        { advertisementTargets: [value.positionId] },
        false,
        useHeaderBidding,
        () => {
          adsToAutoRefresh[key].refreshAutorized = false;
          adsToAutoRefresh[key].intervalToValidation =
            VALIDATION_AUTOREFRESH_VIEW_DURATION;
          handleAutoRefreshValidation(key, true);
        }
      );
    }
  }
}

function initAutoRefresh() {
  if (!BRAND_HAS_DFP_DISPLAY_AUTO_REFRESH) {
    return false;
  }

  if (DIRECT_REFRESH_INTERVAL) {
    setInterval(() => {
      handleAutoRefreshAction(false);
    }, DIRECT_REFRESH_INTERVAL);
  }

  if (PROGRAMMATIC_REFRESH_INTERVAL) {
    setInterval(() => {
      handleAutoRefreshAction(true);
    }, PROGRAMMATIC_REFRESH_INTERVAL);
  }
}

function filterAdsPositionRestriction() {
  const DFP_ID_TO_REMOVE = [];

  if (ADS_RESTRICTION) {
    DFP_ID_TO_REMOVE.push(DFP_INTERSTITIAL);
    DFP_ID_TO_REMOVE.push(DFP_PULSE);
  }

  GPT_LIST_ID.forEach(item => {
    if (
      GPT_CONFIG_PAGE[item].cappingExpireDays &&
      Cookies.get(CAPPING_COOKIE_PREFIX + item)
    ) {
      DFP_ID_TO_REMOVE.push(item);
    }
  });

  return filterAdsIdArray(GPT_LIST_ID, DFP_ID_TO_REMOVE);
}

/**
 * function asked by Wads to disable Dfp preroll if needed in display campaign. (the add #wads_no_position=preroll in the redirection link's ads)
 * Wads used to do it themself when preroll was provided by Wads.
 * @return {bool}  result true if preroll is disabled
 */
function disableDfpPreroll() {
  return /#wads_no_position=preroll/.test(window.location.hash);
}

/**
 * function asked by Wads to disable Dfp postroll if needed in display campaign. (the add #wads_no_position=postroll in the redirection link's ads)
 * Wads used to do it themself when preroll was provided by Wads.
 * @return {bool}  result true if preroll is disabled
 */
function disableDfpPostroll() {
  return /#wads_no_position=postroll/.test(window.location.hash);
}

/**
 * function is used to generate videoAds url for dfp
 * @param {String} pos => should be 'preroll' or 'postroll'
 * @param {Array} targetingFromVideoData => the targeting keys thatwe want to add form the video.
 * @return {String}      result videoAds url
 */
async function getVideoAdslUrl(pos, targetingFromVideoData = []) {
  // START TR NOKTA WRAPPER AROUND DFP...
  if (window.NoktaConfigPlayer && window.NoktaConfigPlayer[pos]) {
    return window.NoktaConfigPlayer[pos];
  }
  // END TR NOKTA WRAPPER AROUND DFP...

  loadGoogleDfp();

  const config = window.DfpConfigPlayer;

  if (!config || !config.tagId || !config.tagLevels) {
    return '';
  }

  let dfpTagId = config.tagId;

  if (BRAND_HAS_GO_IP_ADSERVER && !config.usePlayerSpecificId) {
    dfpTagId = await getTagIdByGeoIp(dfpTagId);
  }

  if (BRAND_HAS_MQ_DFP_FIRST_LEVEL) {
    dfpTagId = `${dfpTagId}_[[MQ_STATE]]`;
  }

  const dfpVideoTag = dfpTagId + config.tagLevels;

  const HASH_KEYWORD_TARGET = document.location.hash;
  const targeting = Array.from(config.targeting || []);

  if (targetingFromVideoData.length) {
    targeting.push(...targetingFromVideoData);
  }

  // If there is any hash on the url. Ad use this to test their ads.
  if (HASH_KEYWORD_TARGET) {
    targeting.push(HASH_KEYWORD_TARGET.split('#')[1]);
  }

  // Tailtarget Segment ads (adorocinema)
  if (typeof window.googletagGetTT === 'function') {
    targeting.push(window.googletagGetTT());
  }

  // Mediarithmics SEGMENTATION PART
  const MediarithmicsSegs = getMediarithmicsKeywords();

  if (MediarithmicsSegs && MediarithmicsSegs !== '') {
    targeting.push(`mics_webediaMics=${MediarithmicsSegs.join(',')}`);
  }

  // NAVEGG
  targeting.push(...setNavEggTargeting(true));

  // TAILTARGET KEYWORDS
  if (window._ttprofiles) {
    const profile = window._ttprofiles;
    targeting.push(`tge=${profile.getEquipment}`);
    targeting.push(`tgp=${profile.getProfiles.join(',')}`);
    targeting.push(`tgu=${profile.getMicrosegments.join(',')}`);
    targeting.push(`tgc=${profile.getSocialClass}`);
    targeting.push(`tga=${profile.getAge}`);
    targeting.push(`tgg=${profile.getGender}`);
    targeting.push(`tgs=${profile.getSubjects.join(',')}`);
    targeting.push(`tgca=${profile.getCustomAudience.join(',')}`);
  }

  // AUDIENCE PROJECT KEYWORDS
  if (getApKeywords()) {
    targeting.push(getApKeywords(true));
  }

  // add position keyword to targeting
  targeting.push(`pos=${pos}`);

  if (BRAND_HAS_DM_PLAYER) {
    if (GPT_CONFIG_PAGE) {
      // /ADUNIT_ID/ADUNIT_TOP_LEVEL/SUBLEVEL (encodé) + / (pas encodé) + key1=value&key2=value1,value2
      dfpTagId = GPT_CONFIG_PAGE['tag-id'];
    }

    if (BRAND_HAS_GO_IP_ADSERVER) {
      dfpTagId = await getTagIdByGeoIp(dfpTagId);
    }

    let dfpAdunitTag = dfpTagId + config.tagLevels;

    if (GPT_CONFIG_PAGE) {
      dfpAdunitTag = dfpTagId + GPT_CONFIG_PAGE['tag-other-levels'];
    }

    return `${encodeURIComponent(dfpAdunitTag)}/${encodeURIComponent(
      targeting.join('&')
    )}`;
  } else {
    const queryString = stringify({
      url: window.location.href,
      correlator: CORRELATOR,
      description_url: window.location.href,
      vpos: pos,
      iu: dfpVideoTag.replace(/\[\[MQ_STATE\]\]/, getMqState()),
      sz: config.size || '640x480',
      env: 'vp',
      gdfp_req: '1',
      impl: 's',
      output: 'vast',
      unviewed_position_start: 1,
      ...(targeting.length > 0 ? { cust_params: targeting.join('&') } : null)
    });

    return `${DFP_BASE_URL}${queryString ? '?' + queryString : ''}`;
  }
}

export async function getPrerollConfiguration(data) {
  if (disableDfpPreroll()) {
    return false;
  }

  const targetingFromVideoData = [];
  const videoDuration = get(data, ['videos', '0', 'duration']);

  if (videoDuration) {
    const duration = parseInt(videoDuration, 10);

    targetingFromVideoData.push(
      `duration_steps=${getAdTrustVideoDurationStepTargeting(duration)}`
    );
  }

  return {
    tag: await getVideoAdslUrl('preroll', targetingFromVideoData)
  };
}

export async function getPostrollConfiguration() {
  if (disableDfpPostroll()) {
    return false;
  }

  return {
    tag: await getVideoAdslUrl('postroll')
  };
}

export function getClientConfiguration() {
  return {
    system: 'dfp',
    client: 'googima',
    vpaidcontrols: true, // Asked by traffic team, put here because we don't want to "pollute" other cases than DFP
    vpaidmode: 'insecure' // Asked by traffic team, put here because we don't want to "pollute" other cases than DFP cf. ALLOCINE-6894
  };
}
